import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { RecordingService } from '../recording/recording-service.interface';
import { RecordingStates } from '../recording/recording-states';
import { NotificationService } from '../notification.service';
import { ActionLoaderComponent } from '../action-loader/action-loader.component';
import { RecorderFactoryService } from '../recording/recorder-factory.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppPropertyName, AppPropertyType, AppSystemProperties } from '../appSystemProperties.service';
import { CardTemplateConfig } from '../components/card-template/card-template.component';

@Component({
  selector: 'app-audio-control',
  templateUrl: './audio-control.component.html',
  styleUrls: ['./audio-control.component.scss'],
})
export class AudioControlComponent implements OnInit, OnDestroy {
  public isRecording = false;
  public recordingState = RecordingStates.STOPED;
  public recordStates = RecordingStates;
  public inputDevices: any;
  public selectedAudio: any;
  public isDisabled = false;

  @Input()
  public auctionId: string;

  @Input()
  public cardConfig: CardTemplateConfig;

  // Configuración por defecto
  public readonly defaultCardConfig: CardTemplateConfig = {
    title: 'Streaming de audio',
    cardClass: 'audio-control-card',
    compact: false
  };

  @ViewChild('loadAudio')
  loadAudio: ActionLoaderComponent;

  @ViewChild('audioSource')
  audioSource: ElementRef<HTMLAudioElement>;
  isAudioSourceVisible = true;
  get $player(): HTMLAudioElement {
    return this.audioSource.nativeElement;
  }

  get finalCardConfig(): CardTemplateConfig {
    return this.cardConfig || this.defaultCardConfig;
  }

  private recordingService: RecordingService;
  private isRecorderConfigFresh = false;
  private recordingSubscriptions = new Subject();

  constructor(
    private recorderFactory: RecorderFactoryService,
    private notificationService: NotificationService,
    private appSysProps: AppSystemProperties,
  ) {}

  ngOnInit(): void {
    this.recorderFactory.getActiveRecorder(this.auctionId, true).then((service) => {
      this.recordingService = service.recorder;
      this.watchRecordingState();
      this.isRecorderConfigFresh = true;
    }).catch(() => this.isDisabled = true);
  }

  ngOnDestroy(): void {
    this.disposeRecordingService();
  }

  onPlayOrPauseRecording() {
    switch (this.recordingState) {
      case RecordingStates.STOPED:
        this.loadAudio.start();
        if (this.isRecorderConfigFresh) {
          this.isRecorderConfigFresh = false;
          this.initializeAudio(this.loadAudio);
          this.muteSource();
          return;
        }

        this.recorderFactory.getActiveRecorder(this.auctionId).then((service) => {
          this.disposeRecordingService();
          this.recordingService = service.recorder;
          this.recordingSubscriptions = new Subject();
          this.watchRecordingState();
          this.initializeAudio(this.loadAudio);
          this.muteSource();
          this.isRecording = true;
        });
        break;
      case RecordingStates.PLAYING:
        this.recordingService.pause();
        break;
      case RecordingStates.PAUSED:
        this.recordingService.play();
        break;
      default:
        break;
    }
  }

  onStopRecording() {
    this.stopRecorder();
  }

  async restart(auctionId: string) {
    if (this.recordingState === RecordingStates.PLAYING || this.recordingState === RecordingStates.PAUSED) {
      await this.stopRecorder();
    }

    this.auctionId = auctionId;
  }

  private stopRecorder() {
    return new Promise<void>(async (resolve) => {
      this.recordingService.stop();
      await this.appSysProps.save(this.auctionId, AppPropertyType.AUDIO, AppPropertyName.IS_STREAMING, false);
      resolve();
    });
  }

  private watchRecordingState() {
    this.recordingService
      .onStateChanged()
      .pipe(takeUntil(this.recordingSubscriptions))
      .subscribe(
        (state) => {
          this.recordingState = state;
          if (state === RecordingStates.STOPED) {
            this.listInputDevices();
          }
        },
        (error) => console.error(error),
      );
  }

  private listInputDevices() {
    this.recordingService
      .getInputDevices()
      .pipe(takeUntil(this.recordingSubscriptions))
      .subscribe((devices) => {
        this.inputDevices = devices;
        this.selectedAudio = devices.length > 0 ? devices[0].deviceId : '';
      });
  }

  private initializeAudio(audioLoader) {
    this.recordingService.init().then(() => {
      this.recordingService
        .start(this.selectedAudio)
        .then(() => {
          this.appSysProps.save(this.auctionId, AppPropertyType.AUDIO, AppPropertyName.IS_STREAMING, true);
        })
        .catch((err) => {
          console.error('Error initializing audio:', err);
          this.notificationService.error('AUDIO_START_ERROR')        
        })
        .finally(() => audioLoader.stop());
    }).catch((err) => {
      console.error('Error initializing audio:', err);
      this.notificationService.error('AUDIO_START_ERROR')        
    })
    .finally(() => audioLoader.stop());
  }

  private muteSource() {
    this.$player.muted = true;
  }

  private disposeRecordingService() {
    this.recordingSubscriptions.next();
    this.recordingSubscriptions.complete();
  }
}
